/**
 * Navigation contract for the full-screen landlord signing route (`/signing/:id/sign`).
 *
 * The signing token never appears here (BR-DS-13): it is fetched by the ceremony page and held in
 * component state only. Only the envelope id (path) and a return target (router state) travel.
 */

/** Router state passed by whoever launches the signing route. */
export interface SigningLaunchState {
  /** Internal path to return to on exit/completion (e.g. the amendment page). */
  returnTo?: string;
}

/** Router state passed back to the return target when the ceremony page hands control back. */
export interface SigningReturnState {
  /** `completed` = the landlord's signature was recorded; `exited` = left without signing. */
  signingResult?: 'completed' | 'exited';
}

export function signingCeremonyPath(envelopeId: string): string {
  return `/signing/${envelopeId}/sign`;
}

export function signingEnvelopePath(envelopeId: string): string {
  return `/signing/${envelopeId}`;
}

/**
 * Accepts only app-internal absolute paths (`/x/y`); anything else (absolute URLs, protocol-relative
 * `//host`, backslash tricks, non-strings) falls back so router state can never cause an open redirect.
 */
export function safeReturnTo(candidate: unknown, fallback: string): string {
  if (typeof candidate !== 'string') return fallback;
  if (!candidate.startsWith('/') || candidate.startsWith('//')) return fallback;
  if (candidate.includes('\\') || candidate.includes('://')) return fallback;
  return candidate;
}

export function readSigningResult(state: unknown): SigningReturnState['signingResult'] | undefined {
  if (!state || typeof state !== 'object') return undefined;
  const result = (state as SigningReturnState).signingResult;
  return result === 'completed' || result === 'exited' ? result : undefined;
}
